import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';

export function Menu({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </button>
      {open ? (
        <div className="menu-pop" onClick={() => setOpen(false)} role="menu">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function MenuItem({
  children,
  onClick,
  shortcut,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  shortcut?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button onClick={onClick} disabled={disabled} role="menuitem">
      <span>{children}</span>
      {shortcut ? <span className="kbd">{shortcut}</span> : null}
    </button>
  );
}

export function MenuCheck({
  children,
  checked,
  onChange,
}: {
  children: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <label
      className="menu-item"
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <span>{children}</span>
      <input type="checkbox" checked={checked} readOnly />
    </label>
  );
}

export const MenuSep = (): JSX.Element => <div className="menu-sep" />;
export const MenuHead = ({ children }: { children: ReactNode }): JSX.Element => (
  <div className="menu-head">{children}</div>
);
